import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import useMap from "./useMap";
import { getMatchingKey} from "./objectUtil";
import {markerDefaultOpts, layerDefaultOpts} from "./staticvariables";

const useMarker = (mapRef) => {

    const {getSource, getLayer, addLayer, removeLayer, removeSource} = useMap(mapRef);

    /**
     * Adds markers to the map with the specified layer options.
     * @param {String} source_id - Source ID for layer to use.
     *
     * @param {Object} marker_layer_options - Options for the adding marker with layer.
     * @param {String} marker_layer_options.id - Marker layer ID to use.
     * @param {String} marker_layer_options.icon_img_id - Image ID for the marker to use.
     * @param {String} marker_layer_options.icon_src - Image source for the marker to use (Supported format: JPEG, PNG).
     * @param {String} marker_layer_options.size - Size of the marker.
     * @param {Object} marker_layer_options.popup - Options for popup (Optional).
     * @param {Boolean} marker_layer_options.popup.isHover - If popup shows via hover.
     
     * @param {Object} camera_options - Options for the camera.
     * @param {Array} camera_options.center - Center coordinates for where to focus layer in map.
     * @param {Number} camera_options.zoom - Zoom scale for layer in map.
     * 
     */
    const addMarkersWithLayer = (source_id, marker_layer_options, camera_options) => {
        let source = getSource(source_id);
        if (!source) throw new Error("No source found.");

        let marker_img_id = marker_layer_options.icon_img_id
        let marker_img = marker_layer_options.icon_src
        let marker_size = marker_layer_options.size || markerDefaultOpts.size

        // 1. Load image marker
        mapRef.current.loadImage(marker_img, (error, image) => {
            if (error) throw error;

            // 1.1 add marker image
            const isImageMarkerExists = mapRef.current.hasImage(marker_img_id)
            if (!isImageMarkerExists) {
                mapRef.current.addImage(marker_img_id, image);
            }

            // 2. Add layer
            let layer_id = marker_layer_options.id;
            const markersLayer = getLayer(layer_id)

            if (!markersLayer) {
                addLayer({
                    id: layer_id,
                    type: 'symbol',
                    source: source.id,
                    layout: {
                        'icon-image': marker_img_id,
                        'icon-size': marker_size,
                    }
                }); 
            }

            // 3. Set camera focus for layer
            let layer_camera_center = camera_options.center || layerDefaultOpts.camera.center
            let layer_camera_zoom = camera_options.zoom || layerDefaultOpts.camera.zoom

            mapRef.current.setCenter(layer_camera_center)
            mapRef.current.setZoom(layer_camera_zoom)

            // 4. Add Popup (optional)
            let hasPopup = marker_layer_options.hasOwnProperty('popup')
            if (hasPopup) {
                const handlePopup = (popup, e) => {
                    const coordinates = e.features[0].geometry.coordinates.slice();
                    const description = e.features[0].properties.description;
        
                    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                    }
        
                    popup.setLngLat(coordinates).setHTML(description).addTo(mapRef.current);
                }

                const popup = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false
                });

                let isHover = marker_layer_options.popup.isHover
                if (isHover) {
                    mapRef.current.on('mouseenter', layer_id, (e) => {
                        mapRef.current.getCanvas().style.cursor = 'pointer';
                        handlePopup(popup, e)
                    });
                } else {
                    mapRef.current.on('mouseenter', layer_id, () => {
                        mapRef.current.getCanvas().style.cursor = 'pointer';
                    });

                    mapRef.current.on('click', layer_id, (e) => {
                        mapRef.current.getCanvas().style.cursor = 'pointer';
                        handlePopup(popup, e)
                    });
                }

                mapRef.current.on('mouseleave', layer_id, () => {
                    mapRef.current.getCanvas().style.cursor = '';
                    popup.remove();
                });
            }
        })
    }

    /**
     * Get markers data with update and remove functions.
     *
     * @param {string} layer_id - The ID of the layer where markers added to.
     */
    const getMarkersWithLayer = (layer_id) => {
        const layer = getLayer(layer_id)
        if (!layer) return null;

        let source_id = layer.source
        const source_data = getSource(source_id)

        const updateData = (newData) => {
            if (source_data) source_data.setData(newData);
        };

        const updateMarkers = (updated_props) => {
            // update camera center focus
            if (updated_props.camera) {
                let layer_camera_center = updated_props.camera.center || layerDefaultOpts.camera.center
                let layer_camera_zoom = updated_props.camera.zoom || layerDefaultOpts.camera.zoom

                mapRef.current.setCenter(layer_camera_center)
                mapRef.current.setZoom(layer_camera_zoom)
            }

            // // update marker image
            // mapRef.current.loadImage(updated_props.icon, (error, image) => {
            //     if (error) throw error;
            //     const img_id = layer.layout['icon-image']
            //     if (mapRef.current.hasImage(img_id)) mapRef.current.updateImage(img_id, image);
            // });

            // // Update for layout property
            // const propKeyInLayout = getMatchingKey(updated_props, layer.layout);
            // if (propKeyInLayout) {
            //     mapRef.current.setLayoutProperty(layer_id, propKeyInLayout, updated_props[propKeyInLayout]);
            // }

            // // Update for paint property
            // const propKeyInPaint = getMatchingKey(updated_props, layer.paint);
            // if (propKeyInPaint) {
            //     mapRef.current.setPaintProperty(layer_id, propKeyInLayout, updated_props[propKeyInLayout]);
            // }
        }

        const removeMarkers = () => {
            removeLayer(layer_id);
            removeSource(source_id);
        }

        return {
            data: {source_data, layer},
            updateData,
            updateMarkers,
            removeMarkers
        };
    }

    return {addMarkersWithLayer, getMarkersWithLayer}
}

export default useMarker